import { useState } from 'react';
import { DollarSign, Save, Calculator, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, planillasApi } from '../lib/api';

interface FilaNomina {
  mecanico_id: string;
  nombre: string;
  remuneracion: number;
  adelantos: number;
  valor_falta: number;
  faltas: number;
  descuento_faltas: number;
  descuento_total: number;
  total: number;
  redondeo: number;
}

const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const Nomina = () => {
  const hoy = new Date();
  const [mes, setMes] = useState(`${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`);
  const [periodo, setPeriodo] = useState<'1-15' | '16-fin'>('1-15');
  const [nomina, setNomina] = useState<FilaNomina[]>([]);
  const [cambios, setCambios] = useState<Set<string>>(new Set());
  const [pagados, setPagados] = useState<Set<string>>(new Set());
  const [cajaPlanilla] = useState<'' | 'mantenimiento' | 'ventas' | 'servicios'>('mantenimiento');   // sueldos salen siempre de Mantenimiento
  const [calculando, setCalculando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [calculado, setCalculado] = useState(false);

  const construirPeriodo = () => {
    const [anio, m] = mes.split('-');
    const [inicio, fin] = periodo.split('-');
    return `${anio}-${m}-${inicio === '1' ? '01' : '16'}/${fin}`;
  };

  const calcular = async () => {
    setCalculando(true);
    setCambios(new Set());
    try {
      const periodoStr = construirPeriodo();
      const [resultado, pagadosDelPeriodo] = await Promise.all([
        api.get<{ periodo: string; nomina: FilaNomina[] }>(`/api/nomina/${encodeURIComponent(periodoStr)}`),
        planillasApi.getPagadosDelPeriodo(periodoStr),
      ]);
      setNomina(resultado.nomina || []);
      setPagados(new Set((pagadosDelPeriodo || []).map((p: any) => p.mecanico_id)));
      setCalculado(true);
    } catch (error: any) {
      console.error('Error calculando nómina:', error);
      toast.error('Error al calcular: ' + error.message);
    } finally {
      setCalculando(false);
    }
  };

  const recalcularFila = (idx: number, campo: 'remuneracion' | 'faltas', valor: number) => {
    setNomina(prev => prev.map((fila, i) => {
      if (i !== idx) return fila;
      const remuneracion = campo === 'remuneracion' ? Math.max(0, valor) : fila.remuneracion;
      const valor_falta = remuneracion / 15;
      const faltas = campo === 'faltas' ? Math.max(0, valor) : fila.faltas;
      const descuento_faltas = faltas * valor_falta;
      const descuento_total = fila.adelantos + descuento_faltas;
      const total = remuneracion - descuento_total;
      return { ...fila, remuneracion, valor_falta, faltas, descuento_faltas, descuento_total, total, redondeo: Math.round(total) };
    }));
    if (campo === 'remuneracion') {
      setCambios(prev => new Set(prev).add(nomina[idx].mecanico_id));
    }
  };

  const descargarPdf = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
      const response = await fetch(`${API_URL}/api/nomina/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ periodo: construirPeriodo(), nomina })
      });
      if (!response.ok) throw new Error('No se pudo generar el PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nomina_${construirPeriodo().replace(/\//g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error descargando PDF de nómina:', error);
      toast.error('No se pudo generar el PDF de la nómina');
    }
  };

  // Guardar deja un registro permanente en "planillas" (histórico, no editable) y un
  // gasto en Caja por cada mecánico pagado; los mecánicos que ya tenían una planilla
  // guardada para este mismo periodo se omiten automáticamente (no se cobran dos veces).
  const guardar = async () => {
    if (!cajaPlanilla) {
      toast.error('Elegí de qué caja sale el pago de esta planilla');
      return;
    }
    const periodoStr = construirPeriodo();
    const pendientes = nomina.filter(f => !pagados.has(f.mecanico_id));
    if (pendientes.length === 0) {
      toast.error('Todos los mecánicos de este periodo ya tienen su planilla guardada');
      return;
    }
    const nombreCaja = cajaPlanilla === 'mantenimiento' ? 'Mantenimiento' : cajaPlanilla === 'servicios' ? 'Servicios' : 'Ventas';
    if (!confirm(`¿Guardar la planilla de ${pendientes.length} mecánico(s) para el periodo ${periodoStr}, desde la Caja de ${nombreCaja}? Esto registra el gasto en Caja y no se puede deshacer desde acá.`)) return;

    setGuardando(true);
    try {
      const resultado = await planillasApi.guardar({ periodo: periodoStr, nomina: pendientes, caja: cajaPlanilla });
      setCambios(new Set());
      setPagados(prev => {
        const nuevo = new Set(prev);
        pendientes.forEach(f => nuevo.add(f.mecanico_id));
        return nuevo;
      });
      await descargarPdf();
      const mensajeOmitidos = resultado.omitidos.length > 0
        ? ` (${resultado.omitidos.length} ya estaban pagados: ${resultado.omitidos.join(', ')})`
        : '';
      toast.success(`Planilla guardada: ${resultado.procesados} mecánico(s)${mensajeOmitidos}`);
    } catch (error: any) {
      console.error('Error guardando planilla:', error);
      toast.error('Error al guardar la planilla: ' + error.message);
    } finally {
      setGuardando(false);
    }
  };

  const totales = nomina.reduce((acc, n) => ({
    sueldo: acc.sueldo + n.remuneracion,
    adelantos: acc.adelantos + n.adelantos,
    descuentoTotal: acc.descuentoTotal + n.descuento_total,
    total: acc.total + n.redondeo
  }), { sueldo: 0, adelantos: 0, descuentoTotal: 0, total: 0 });

  const hayPendientes = nomina.some(f => !pagados.has(f.mecanico_id));

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <DollarSign className="text-green-600" />
          Planillas
        </h1>
        <p className="text-gray-600 mt-1">Registro de pago quincenal a mecánicos y personal del taller</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-3">
        <select value={mes} onChange={(e) => setMes(e.target.value)} className="border rounded-lg px-3 py-2">
          {meses.map((nombre, i) => {
            const valor = `${hoy.getFullYear()}-${String(i + 1).padStart(2, '0')}`;
            return <option key={valor} value={valor}>{nombre} {hoy.getFullYear()}</option>;
          })}
        </select>
        <select value={periodo} onChange={(e) => setPeriodo(e.target.value as '1-15' | '16-fin')} className="border rounded-lg px-3 py-2">
          <option value="1-15">1ra Quincena</option>
          <option value="16-fin">2da Quincena</option>
        </select>
        <span className="border rounded-lg px-3 py-2 bg-gray-50 text-gray-700">Sale de: Caja de Mantenimiento</span>
        <button
          onClick={calcular}
          disabled={calculando}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Calculator size={18} />
          {calculando ? 'Calculando...' : 'Calcular'}
        </button>
        <button
          onClick={guardar}
          disabled={guardando || nomina.length === 0 || !hayPendientes || !cajaPlanilla}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Save size={18} />
          {guardando ? 'Guardando...' : 'Guardar planilla y generar PDF'}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        {!calculado ? (
          <p className="text-center py-12 text-gray-500">Seleccioná el periodo y presioná Calcular</p>
        ) : nomina.length === 0 ? (
          <p className="text-center py-12 text-gray-500">No hay mecánicos activos</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-right">Sueldo quincena</th>
                <th className="px-3 py-2 text-right">Adelantos</th>
                <th className="px-3 py-2 text-right">Valor x día</th>
                <th className="px-3 py-2 text-center">Faltas</th>
                <th className="px-3 py-2 text-right">Desc. Faltas</th>
                <th className="px-3 py-2 text-right">Desc. Total</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right">Redondeo</th>
                <th className="px-3 py-2 text-center">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {nomina.map((fila, idx) => {
                const yaPagado = pagados.has(fila.mecanico_id);
                return (
                <tr key={fila.mecanico_id} className={`hover:bg-gray-50 ${yaPagado ? 'bg-green-50' : cambios.has(fila.mecanico_id) ? 'bg-yellow-50' : ''}`}>
                  <td className="px-3 py-2">{idx + 1}</td>
                  <td className="px-3 py-2 font-medium">{fila.nombre}</td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={fila.remuneracion}
                      disabled={yaPagado}
                      onChange={(e) => recalcularFila(idx, 'remuneracion', parseFloat(e.target.value) || 0)}
                      className="w-24 text-right border rounded px-2 py-1 disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-3 py-2 text-right text-red-600">S/ {fila.adelantos.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-gray-500">S/ {fila.valor_falta.toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">
                    <input
                      type="number"
                      min="0"
                      value={fila.faltas}
                      disabled={yaPagado}
                      onChange={(e) => recalcularFila(idx, 'faltas', parseInt(e.target.value) || 0)}
                      className="w-14 text-center border rounded px-2 py-1 disabled:bg-gray-100"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">S/ {fila.descuento_faltas.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">S/ {fila.descuento_total.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-green-600">S/ {fila.total.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-bold">S/ {fila.redondeo.toFixed(2)}</td>
                  <td className="px-3 py-2 text-center">
                    {yaPagado ? (
                      <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                        <CheckCircle2 size={14} /> Pagado
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Pendiente</span>
                    )}
                  </td>
                </tr>
                );
              })}
              <tr className="bg-blue-50 font-bold">
                <td className="px-3 py-2" colSpan={2}>TOTALES</td>
                <td className="px-3 py-2 text-right">S/ {totales.sueldo.toFixed(2)}</td>
                <td className="px-3 py-2 text-right">S/ {totales.adelantos.toFixed(2)}</td>
                <td colSpan={3}></td>
                <td className="px-3 py-2 text-right">S/ {totales.descuentoTotal.toFixed(2)}</td>
                <td></td>
                <td className="px-3 py-2 text-right">S/ {totales.total.toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Nomina;
